/**
 * @fileoverview Virtual Test Center Login Screen
 * 
 * Provides authentication interface for proctors accessing the Virtual Test Center.
 * Handles both initial login and automatic authentication via stored tokens.
 */

import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Flex,
  TextInput,
  Button,
  View,
  Alert,
  Text,
  FormFieldGroup,
  FormField,
  IconEmailLine,
  IconLockLine,
} from '@smarterservices/smarter-ui';
import { login } from '../../lib/api';
import useAppStore from '../../stores/app';
import { ROUTES } from '../../config';
import { useAuth } from '../../context/AuthContext';
import useTranslations from '@shared/hooks/useTranslations';
import { replacePathPlaceholders } from '../../helpers/utilities';
import PageLoader from '../../helpers/loader';
import VTCLogo from './VTCLogo';

/**
 * LoginScreen Component
 * 
 * Renders the Virtual Test Center login interface with email/password authentication.
 * Automatically checks for existing authentication tokens and redirects authenticated users.
 * 
 * @component
 * @returns {JSX.Element} The login screen UI
 * 
 * @example
 * // Used in routing configuration
 * <Route path="/vtc" element={<LoginScreen />} />
 */
const LoginScreen = () => {
  const navigate = useNavigate();
  const authenticate = useAppStore((state) => state.authenticate);
  const loginScreenMessage = useAppStore((state) => state.loginScreenMessage);
  const { check } = useAuth();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertMessageType, setAlertMessageType] = useState('error');
  // Refs for accessing input values
  const usernameRef = useRef();
  const passwordRef = useRef();
  const { t } = useTranslations();

  /**
   * Effect: Handle automatic authentication check and login screen messages
   * 
   * - Displays any pending login screen messages (e.g., from logout)
   * - Checks for existing authentication token if accessing /vtc directly
   * - Redirects authenticated users to rooms list or specific room
   */
  useEffect(() => {
    // Display login screen message if present (e.g., "You have been logged out")
    if (loginScreenMessage) {
      setAlertMessage(loginScreenMessage.message);
      setAlertMessageType(loginScreenMessage.type);
      useAppStore.getState().setLoginScreenMessage(null);
    }

    /**
     * Check if user is already authenticated via stored token
     * @async
     */
    const checkLoader = async () => {
      const checkResult = await check({ redirectOnFailure: false });

      if (checkResult.successful) {
        // Redirect to specific room if roomSid is present, otherwise to rooms list
        if (checkResult.roomSid) {
          navigate(
            replacePathPlaceholders(ROUTES.ROOM, { roomSid: checkResult.roomSid })
          );
        } else {
          navigate(ROUTES.ROOMS);
        }
      } else {
        setPageLoaded(true);
      }
    };

    // Only check authentication if accessing /vtc directly (not from other routes)
    if (location.pathname === '/vtc') {
      checkLoader();
    } else {
      setPageLoaded(true);
    }
  }, []);

  /**
   * Handle login form submission
   * 
   * Authenticates user credentials and redirects to rooms list on success.
   * Displays error message if authentication fails.
   * 
   * @async
   * @param {Event} event - Form submit event
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsLoading(true);
    // Call login API with email and password
    const loginResult = await login(
      usernameRef.current.value,
      passwordRef.current.value
    );

    if (loginResult.successful) {
      // Store authentication token and navigate to rooms list
      await authenticate(loginResult.data.authentication.token);
      navigate(ROUTES.ROOMS);
    } else {
      setIsLoading(false);
      // Display authentication error message
      setAlertMessageType('error');
      setAlertMessage('Your credentials could not be authenticated');
    }
  };

  return (
    <>
      {pageLoaded ? (
        <View as="div" height="100vh" background="secondary">
          <Flex direction="column" justifyItems="center" alignItems="center" height="100%">
            <Flex.Item>
              <View
                as="div"
                width="500px"
                margin="large"
                background="primary"
                borderColor="primary"
                borderWidth="small"
                padding="large"
                borderRadius="medium"
                shadow="topmost"
              >
                <form onSubmit={handleSubmit}>
                  {/* Page title */}
                  <Flex direction="column" alignItems="center" margin="0 0 0 0">
                    <View as="div" margin="0 0 small 0">
                      <VTCLogo color="#424242" width="200px" height="200px" />
                    </View>
                  </Flex>

                  {/* Display alert messages (errors, logout notifications, etc.) */}
                  {alertMessage && (
                    <Alert
                      variant={alertMessageType}
                      renderCloseButtonLabel="Close"
                      margin="small small medium small"
                      onDismiss={() => setAlertMessage('')}
                      timeout={5000}
                    >
                      {alertMessage}
                    </Alert>
                  )}

                  {/* Login form fields */}
                  <FormFieldGroup description="" layout="stacked">
                    <FormField
                      id="username"
                      color="primary-inverse"
                      label={t('login.email.label', 'Email')}
                    >
                      <TextInput
                        id="username"
                        type="email"
                        placeholder={t('login.email.placeholder', 'Enter your email')}
                        inputRef={(ref) => (usernameRef.current = ref)}
                        renderBeforeInput={<IconEmailLine />}
                      />
                    </FormField>

                    <FormField id="password" label={t('login.password.label', 'Password')}>
                      <TextInput
                        id="password"
                        type="password"
                        placeholder={t('login.password.placeholder', 'Enter your password')}
                        inputRef={(ref) => (passwordRef.current = ref)}
                        renderBeforeInput={<IconLockLine />}
                      />
                    </FormField>
                  </FormFieldGroup>

                  {/* Submit button */}
                  <Button
                    type="submit"
                    color="primary"
                    display="block"
                    width="100%"
                    margin="medium 0 0 0"
                    disabled={isLoading}
                  >
                    {isLoading
                      ? t('login.signingIn', 'Signing in...')
                      : t('login.signIn', 'Sign in')}
                  </Button>
                </form>
              </View>
            </Flex.Item>
            {typeof __APP_VERSION__ !== 'undefined' &&
              <View as="div" textAlign="center">
                <Text size="x-small" color="secondary">
                  v{__APP_VERSION__}
                  {typeof __APP_MODE__ !== 'undefined' && __APP_MODE__ !== 'production' && (
                    <> ({__APP_MODE__})</>
                  )}
                </Text>
              </View>
            }
          </Flex>
        </View >
      ) : (
        <PageLoader text="Checking Authentication..." />
      )}
    </>
  );
};

export default LoginScreen;
